/**
 * Computes default/initial values for the Details step form.
 * Keys MUST match the field `name` in useDetailsFormSchema.
 *
 * Reads from payloadRef.current.details — which is pre-populated by
 * useWizardInitialValues before the form mounts for all three paths
 * (adhoc defaults / plan hydration / edit hydration).
 *
 * Runtime overlays applied here (not stored in payloadRef):
 *   - candidatePhoneValue: resolves async from applicationData.contact
 *   - location fallback: async from useLocationFieldConfig when plan
 *     has no location
 */
import { useMemo } from 'react';
import { useLocationFieldConfig } from '@/formConfigs';
import type {
  ScheduleMode,
  ScheduleInterviewPayload,
  PhoneValue,
} from '../../../config/types';
import { isSelfScheduleMode } from '../../../config/modeClassifiers';

export type { PhoneValue };

export interface CalendarInfoFieldValue {
  provider_calendar_id: string;
  is_primary?: boolean;
}

export interface DetailsFormValues {
  [key: string]: unknown;
  interviewName: string;
  interview_type: string;
  participant_ids: string | string[];
  book_meeting_room: boolean;
  meeting_room_ids: string;
  calendar_info: CalendarInfoFieldValue | null;
  duration: number;
  dateTime: string;
  startDate: Date | null;
  startTime: string;
  endTime: string;
  isDateTimeEditing: string;
  isCalendarEditing: string;
  /** String companion to `calendar_info` — feeds the native edit Select. */
  calendar_info_picker: string;
  findTime: string;
  calendar_visibility: boolean;
  location: string;
  host_id: string;
  ats_office_id: string;
  candidatePhone: string;
  candidate_phone: PhoneValue | undefined;
  isCandidatePhoneEditing: string;
  enable_note_taker: boolean;
  ext_interview_template_id: string | null;
}

interface UseDetailsFormDefaultsOptions {
  candidatePhoneValue?: PhoneValue;
}

export function useDetailsFormDefaults(
  payloadRef: React.MutableRefObject<ScheduleInterviewPayload>,
  mode: ScheduleMode,
  options?: UseDetailsFormDefaultsOptions
): DetailsFormValues {
  const isSelfSchedule = isSelfScheduleMode(mode);
  const candidatePhoneValue = options?.candidatePhoneValue;
  const { defaultLocation: availableDefault } = useLocationFieldConfig({});

  return useMemo(() => {
    const d = payloadRef.current.details as Record<string, unknown>;

    const interview_type = (d.interview_type as string) ?? 'one_on_one';
    const isPanel = interview_type === 'panel';

    // participant_ids shape must match the schema's isMulti flag, else
    // Zod throws "Expected array, received string" (or vice-versa).
    // Multi-select for self-schedule and panel; scalar otherwise.
    const rawIds = d.participant_ids;
    const isMulti = isSelfSchedule || isPanel;
    let participant_ids: string | string[];
    if (Array.isArray(rawIds)) {
      participant_ids = isMulti ? rawIds : (rawIds[0] ?? '');
    } else {
      const scalar = (rawIds as string | undefined) ?? '';
      participant_ids = isMulti ? (scalar ? [scalar] : []) : scalar;
    }

    // Location: use stored value if present, fall back to async account default.
    const location = (d.location as string) || (availableDefault ?? '');

    // Phone: runtime prop may arrive after mount (applicationData resolves async).
    const candidate_phone =
      candidatePhoneValue ?? (d.candidate_phone as PhoneValue | undefined);
    const isCandidatePhoneEditing = candidatePhoneValue
      ? 'view'
      : ((d.isCandidatePhoneEditing as string) ?? 'edit');

    const calendar_info =
      (d.calendar_info as CalendarInfoFieldValue | null) ?? null;

    return {
      interviewName: (d.interviewName as string) ?? '',
      interview_type,
      participant_ids,
      participant_default_options:
        (d.participant_default_options as unknown[]) ?? [],
      duration: (d.duration as number) ?? 30,
      location,
      host_id: (d.host_id as string) ?? '',
      ats_office_id: (d.ats_office_id as string) ?? '',
      book_meeting_room: Boolean(d.book_meeting_room),
      // Single-select field — coerce legacy array shapes (older payload
      // hydrations) down to the first email so the FormBuilder Zod
      // schema (z.string()) doesn't reject an array value at validation.
      meeting_room_ids: Array.isArray(d.meeting_room_ids)
        ? ((d.meeting_room_ids[0] as string | undefined) ?? '')
        : ((d.meeting_room_ids as string | undefined) ?? ''),
      calendar_info,
      calendar_info_picker: calendar_info?.provider_calendar_id ?? '',
      dateTime: '',
      startDate: null,
      startTime: '',
      endTime: '',
      isDateTimeEditing: 'view',
      isCalendarEditing: 'view',
      findTime: '',
      calendar_visibility: Boolean(d.calendar_visibility),
      candidatePhone: '',
      candidate_phone,
      isCandidatePhoneEditing,
      enable_note_taker: Boolean(d.enable_note_taker),
      ext_interview_template_id:
        (d.ext_interview_template_id as string | null) ?? null,
    };
    // payloadRef is a stable ref — content is set before form mounts.
    // availableDefault and candidatePhoneValue are the only reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDefault, candidatePhoneValue, isSelfSchedule]);
}
